import MaterialTable from '@material-table/core';
import { Box, Button, Card, CardContent, Grid, TextField } from '@mui/material';
import { ChangeCircleOutlined, HistoryEduOutlined, PublishedWithChangesOutlined, UnpublishedOutlined } from '@mui/icons-material';
import { PageAppBar } from '../../components/pageAppBar';
import { useContext, useEffect, useState } from 'react';
import { actionOneButtonStyle, cardStyle } from '../../theme';
import moment from 'moment';
import { generateClient } from 'aws-amplify/api';
import { tenantLogs } from '../../graphql/queries';
import { UserContext } from '../../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import { HistoryDialog } from '../../components/admin/historyDialog';
import { fetchAllPages } from '../../tools/graphqlPaging';

export function AuditLog() {
	const client = generateClient();

	const userContext = useContext(UserContext);
	const navigate = useNavigate();
	const [items, setItems] = useState([]);
	const [fetchingLogs, setFetchingLogs] = useState(false);
	const [startDate, setStartDate] = useState(
		moment().subtract(7, 'days').startOf('day').toISOString(),
	);
	const [endDate, setEndDate] = useState(moment().endOf('day').toISOString());

	const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
	const [selectedDocument, setSelectedDocument] = useState(null);

	useEffect(() => {
		if (userContext.tenantId) {
			getAuditLogs();
		}
	}, [userContext.tenantId]);

	async function getAuditLogs() {
		setFetchingLogs(true);
		try {
			const logItems = await fetchAllPages({
				client,
				query: tenantLogs,
				resultKey: 'tenantLogs',
				variables: {
					tenant: userContext.tenantId,
					timestamp: {
						between: [startDate, endDate],
					},
					sortDirection: 'DESC',
				},
			});

			setItems(logItems);
		} catch (e) {
			console.error('Error getting logs', e);
		}
		setFetchingLogs(false);
	}

	return (
		<>
			<PageAppBar
				title="Audit Log"
				description="View history of changes for the main application data"
			/>
			<Grid container direction="column">
				<Grid item>
					<Card variant="outlined" sx={cardStyle}>
						<CardContent>
							<Grid container direction="column">
								<Grid item>
									<Box display="flex" justifyContent="end" gap={3}>
										<Box>
											<TextField
												type="date"
												label="Select Start Date"
												value={moment(startDate).format('YYYY-MM-DD')}
												inputProps={{
													min: moment()
														.subtract(120, 'days')
														.format('YYYY-MM-DD'),
													max: moment().format('YYYY-MM-DD'),
												}}
												onChange={(e) => {
													setStartDate(
														moment(e.target.value).startOf('day').toISOString(),
													);
												}}
												InputLabelProps={{
													shrink: true,
												}}
											/>
										</Box>
										<Box>
											<TextField
												type="date"
												label="Select End Date"
												value={moment(endDate).format('YYYY-MM-DD')}
												inputProps={{
													min: moment()
														.subtract(120, 'days')
														.format('YYYY-MM-DD'),
													max: moment().format('YYYY-MM-DD'),
												}}
												onChange={(e) => {
													setEndDate(
														moment(e.target.value).endOf('day').toISOString(),
													);
												}}
												InputLabelProps={{
													shrink: true,
												}}
											/>
										</Box>
										<Box>
											<Button
												sx={actionOneButtonStyle}
												onClick={() => {
													getAuditLogs();
												}}
											>
												Search
											</Button>
										</Box>
									</Box>
								</Grid>
								<Grid item>
									<MaterialTable
										title=""
										components={{
											Container: (props) => <Box {...props} elevation={0} />,
										}}
										columns={[
											{
												title: 'Timestamp',
												field: 'timestamp',
												width: '275px',
												render: (rowData) => (
													<Box display="flex">
														{moment(rowData.timestamp).format('L LT')}
													</Box>
												),
											},
											{
												title: 'Type',
												field: 'type',
												width: '300px',
												render: (rowData) => {
													switch (rowData.type) {
														case 'Campaign':
															return 'Campaign';
														case 'Segment':
															return 'Contact Segment';
														case 'Suppression':
															return 'Suppression Profile';
														case 'Profile':
															return 'Communication Profile';
														case 'ManualProfile':
															return 'Manual Dialing Profile';
														case 'ScheduledBulkOperation':
															return 'Scheduled Bulk Operation';
														default:
															return rowData.type;
													}
												},
											},
											{
												title: 'Document',
												field: 'documentId',
												render: (rowData) => (
													<Box display="flex">
														<>
															{rowData.action !== 'DELETED' && (
																<>
																	{rowData.type === 'Segment' && (
																		<Button
																			variant="text"
																			onClick={() => {
																				navigate(
																					`/segments/${rowData.documentId}?op=edit`,
																				);
																			}}
																		>
																			{rowData.name ? rowData.name : rowData.documentId}
																		</Button>
																	)}
																	{rowData.type === 'Suppression' && (
																		<Button
																			variant="text"
																			onClick={() => {
																				navigate(
																					`/suppression/${rowData.documentId}`,
																				);
																			}}
																		>
																			{rowData.name ? rowData.name : rowData.documentId}
																		</Button>
																	)}
																	{rowData.type === 'Profile' && (
																		<Button
																			variant="text"
																			onClick={() => {
																				navigate(
																					`/profiles/${rowData.documentId}`,
																				);
																			}}
																		>
																			{rowData.name ? rowData.name : rowData.documentId}
																		</Button>
																	)}
																	{rowData.type === 'ManualProfile' && (
																		<Button
																			variant="text"
																			onClick={() => {
																				navigate(
																					`/manual-profiles/${rowData.documentId}`,
																				);
																			}}
																		>
																			{rowData.name ? rowData.name : rowData.documentId}
																		</Button>
																	)}
																	{rowData.type === 'ScheduledBulkOperation' && (
																		<Button
																			variant="text"
																			onClick={() => {
																				navigate('/bulk-edit');
																			}}
																		>
																			{rowData.name ? rowData.name : rowData.documentId}
																		</Button>
																	)}
																	{rowData.type === 'Campaign' &&
																		(rowData.name ? rowData.name : rowData.documentId)}
																</>
															)}
															{rowData.action === 'DELETED' ? (rowData.name ? rowData.name : rowData.documentId) : ''}
														</>
													</Box>
												),
											},
											{
												title: 'Action',
												field: 'action',
												width: '150px',
												render: (rowData) => (
													<Box display="flex" alignItems="center">
														{rowData.action === 'CREATED' && (
															<Box
																display="flex"
																alignItems="center"
																gap={1}
															>
																<PublishedWithChangesOutlined
																	color="success"
																	fontSize="small"
																/>
																Created
															</Box>
														)}
														{rowData.action === 'UPDATED' && (
															<Box
																display="flex"
																alignItems="center"
																gap={1}
															>
																<ChangeCircleOutlined
																	color="primary"
																	fontSize="small"
																/>
																Updated
															</Box>
														)}
														{rowData.action === 'DELETED' && (
															<Box
																display="flex"
																alignItems="center"
																gap={1}
															>
																<UnpublishedOutlined
																	color="error"
																	fontSize="small"
																/>
																Deleted
															</Box>
														)}
													</Box>
												),
											},
											{ title: 'User Name', field: 'userName' },

										]}
										data={items}
										options={{
											actionsColumnIndex: -1,
											pageSize: 10,
											searchText:
												window.sessionStorage.getItem('ListAuditLogSearch'),
										}}
										isLoading={fetchingLogs}
										actions={[
											{
												icon: () => <HistoryEduOutlined color="primary" />,
												tooltip: 'View Document History',
												onClick: (event, data) => {
													setSelectedDocument(data);
													setHistoryDialogOpen(true);
												},
											},
										]}
									></MaterialTable>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
			<HistoryDialog
				open={historyDialogOpen}
				document={selectedDocument}
				onClose={() => {
					setHistoryDialogOpen(false);
					setSelectedDocument(null);
				}}
			/>
		</>
	);
}
